import { parseISO, isValid } from 'date-fns';
import type { CropNameSource } from './cropName';
import { formatDate } from './locale-formatting';

// FIX-SEASON-FIRST — an organisation onboards a farmer into a Season, and the Season decides
// the crop. This file holds the farmer-facing pieces that do not need React or supabase:
// the season's name in the farmer's language, and which "start a cycle" screen to show.

/** The name fields of a season (season_catalogue, as returned by fn_my_open_seasons or a direct read). */
export interface SeasonNameSource {
  names?: unknown;
  name_lo?: string | null;
  name_en?: string | null;
}

/**
 * The name a farmer sees for a season:
 *   1. season_catalogue.names[lang]
 *   2. name_lo, when the language is Lao (the column that existed before `names`)
 *   3. name_en
 * Returns '' only when there is no season.
 */
export const seasonName = (season: SeasonNameSource | null | undefined, lang: string | null | undefined): string => {
  if (!season) return '';
  const language = (lang || '').trim().toLowerCase().split('-')[0];
  const names = season.names;
  if (language && names && typeof names === 'object' && !Array.isArray(names)) {
    const fromNames = (names as Record<string, unknown>)[language];
    if (typeof fromNames === 'string' && fromNames.trim()) return fromNames.trim();
  }
  if (language === 'lo' && season.name_lo && season.name_lo.trim()) return season.name_lo.trim();
  return season.name_en ?? '';
};

/** One season from fn_my_open_seasons. */
export interface OpenSeason extends SeasonNameSource {
  season_id: string;
  org_name: string | null;
  region: string | null;
  window_from: string | null;
  window_to: string | null;
  crop: (CropNameSource & { id?: string; icon?: string | null }) | null;
  template: { id: string; total_days: number } | null;
  /** FIX-SEASON-CARD-POLISH: the server's answer to "can this farmer start today?" (absent on older servers). */
  can_start?: boolean;
  start_blocked_reason?: StartBlockedReason | null;
}

export type StartBlockedReason = 'WINDOW_CLOSED' | 'NOT_OPEN_YET';

/**
 * A date as farmers read it: day/month/year, Arabic numerals ("01/06/2026").
 * Takes a yyyy-MM-dd string (read as a local date, never shifted by time zone) or a Date.
 * Returns '' for a missing or unreadable value.
 */
export const formatDayMonth = (value: string | Date | null | undefined): string => {
  if (!value) return '';
  const d = typeof value === 'string' ? parseISO(value) : value;
  if (!isValid(d)) return '';
  return formatDate(d, 'dd/MM/yyyy');
};

/** One published catalogue entry for the farmer's country (the fallback when the farmer is in no season). */
export interface CatalogueEntry extends SeasonNameSource {
  id: string;
  region: string | null;
  start_window_from: string | null;
  start_window_to: string | null;
  cycle_template_id: string;
  crops: (CropNameSource & { id?: string; icon?: string | null }) | null;
  crop_cycles: { id: string; total_days: number } | null;
}

/** One thing the farmer can start, whichever list it came from. */
export interface StartOption {
  key: string;
  /** 'season' = the farmer's organisation put them in it; 'catalogue' = a published season for the country. */
  kind: 'season' | 'catalogue';
  /** Set only for kind 'season' — the cycle is started through fn_start_season_cycle. */
  seasonId: string | null;
  /** Set only for kind 'catalogue' — the cycle is started through fn_start_catalogue_cycle. */
  catalogueId: string | null;
  templateId: string;
  totalDays: number;
  season: SeasonNameSource;
  crop: (CropNameSource & { id?: string; icon?: string | null }) | null;
  orgName: string | null;
  windowFrom: string | null;
  windowTo: string | null;
  /** Why the farmer cannot start this season today (server rule); null = can start. Seasons only. */
  blocked: StartBlockedReason | null;
}

export type StartMode =
  /** Exactly one open season: no list — a season card and Start. */
  | 'season-one'
  /** Several open seasons: list only those. */
  | 'season-many'
  /** In no season: the country's published seasons, by season name. */
  | 'catalogue'
  /** In no season and the country has no published season: the template list, as before. */
  | 'templates';

export interface StartPlan {
  mode: StartMode;
  options: StartOption[];
}

/**
 * Decide what the "start a cycle" screen shows.
 * The farmer's own seasons always win; the country catalogue is only a fallback; the raw
 * template list is only used when the country has no published season at all.
 */
export const planStart = (seasons: OpenSeason[] | null | undefined, catalogue: CatalogueEntry[] | null | undefined): StartPlan => {
  const mine: StartOption[] = (seasons ?? [])
    .filter((s) => !!s.template?.id)
    .map((s) => ({
      key: `season:${s.season_id}`,
      kind: 'season' as const,
      seasonId: s.season_id,
      catalogueId: null,
      templateId: s.template!.id,
      totalDays: s.template!.total_days,
      season: { names: s.names, name_lo: s.name_lo, name_en: s.name_en },
      crop: s.crop,
      orgName: s.org_name,
      windowFrom: s.window_from,
      windowTo: s.window_to,
      blocked: s.can_start === false ? (s.start_blocked_reason ?? 'WINDOW_CLOSED') : null,
    }));
  if (mine.length === 1) return { mode: 'season-one', options: mine };
  if (mine.length > 1) return { mode: 'season-many', options: mine };

  const published: StartOption[] = (catalogue ?? [])
    .filter((c) => !!c.crop_cycles?.id)
    .map((c) => ({
      key: `catalogue:${c.id}`,
      kind: 'catalogue' as const,
      seasonId: null,
      catalogueId: c.id,
      templateId: c.crop_cycles!.id,
      totalDays: c.crop_cycles!.total_days,
      season: { names: c.names, name_lo: c.name_lo, name_en: c.name_en },
      crop: c.crops,
      orgName: null,
      windowFrom: c.start_window_from,
      windowTo: c.start_window_to,
      blocked: null,
    }));
  if (published.length > 0) return { mode: 'catalogue', options: published };

  return { mode: 'templates', options: [] };
};

const iso = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * The start dates the farmer may pick (yyyy-MM-dd), mirroring fn_start_season_cycle and
 * fn_start_catalogue_cycle (FIX-SEASON-GATE: the catalogue start obeys the window too):
 * never in the future; inside the season's window when it has one; otherwise the last 30 days.
 * `open` is false when the season's window has not begun yet — nothing can be started today.
 * `closed` is true when the window ended before today — the server refuses SEASON_WINDOW_CLOSED.
 */
export const startDateBounds = (
  option: Pick<StartOption, 'kind' | 'windowFrom' | 'windowTo'> | null | undefined,
  today: Date,
): { min: string; max: string; open: boolean; closed: boolean } => {
  const todayIso = iso(today);
  const thirty = new Date(today);
  thirty.setDate(thirty.getDate() - 30);
  const fallback = { min: iso(thirty), max: todayIso, open: true, closed: false };

  if (!option || (!option.windowFrom && !option.windowTo)) return fallback;

  const min = option.windowFrom ?? fallback.min;
  const max = option.windowTo && option.windowTo < todayIso ? option.windowTo : todayIso;
  const closed = !!option.windowTo && option.windowTo < todayIso;
  return { min, max, open: min <= max, closed };
};
